import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, CloudUpload, Download, FileSpreadsheet, FileText, Paperclip, X } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { PdfPreview } from '@/components/pdf-preview'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { fileAccept, formatFileSize, isSpreadsheet, MAX_IMPORT_BYTES, quoteFilesKey, type QuoteFile } from './api'
import { SpreadsheetPreview } from './spreadsheet-preview'
import { useQuoteFiles, useQuoteFilesApi } from './use-quote-files'
import { getApiErrorMessage } from '@/lib/api-error'

// The quote's Files library (USA-1023), ported from the demo's features/files/quote-files.tsx.
// Uploading never changes the quote's revision or stages.

const IMPORTABLE = /\.(pdf|xlsx|csv|png|jpe?g)$/i

/** The paperclip: opens the system file chooser (demo `FilePicker`). */
export function FilePicker({ add, disabled, compact = false }: { add: (files: File[]) => unknown; disabled?: boolean; compact?: boolean }) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        accept={fileAccept}
        aria-label="Choose quote files"
        className="sr-only"
        disabled={disabled}
        multiple
        onChange={(event) => {
          const files = Array.from(event.target.files || [])
          event.target.value = ''
          if (files.length) void add(files)
        }}
        ref={input}
        tabIndex={-1}
        type="file"
      />
      <Button
        aria-label={compact ? 'Attach files' : undefined}
        disabled={disabled}
        onClick={() => input.current?.click()}
        size={compact ? 'icon' : 'sm'}
        title="PDF, Excel, CSV, text, AutoQuotes or images, up to 50 MB each"
        variant="outline"
      >
        <Paperclip aria-hidden="true" />
        {compact ? null : 'Add files'}
      </Button>
    </>
  )
}

function useFileUrl(file: QuoteFile, enabled = true) {
  const files = useQuoteFilesApi()
  return useQuery({
    queryKey: ['quote-file-url', file.id],
    queryFn: ({ signal }) => files.url(file.id, signal),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

function FileIcon({ file }: { file: QuoteFile }) {
  const image = file.content_type.startsWith('image/')
  const url = useFileUrl(file, image)
  const [failed, setFailed] = useState(false)
  if (image && url.data && !failed) {
    return <img alt="" className="size-10 shrink-0 rounded-md border object-cover" loading="lazy" onError={() => setFailed(true)} src={url.data.url} />
  }
  const Icon = isSpreadsheet(file) || file.content_type === 'text/csv' ? FileSpreadsheet : FileText
  return (
    <span className="grid size-10 shrink-0 place-items-center rounded-md border bg-muted/50 text-muted-foreground">
      <Icon aria-hidden="true" className="size-4" />
    </span>
  )
}

/** The file itself: image, PDF pages, workbook sheets or text (demo `FilePreview`). */
export function FilePreview({ file }: { file: QuoteFile }) {
  const files = useQuoteFilesApi()
  const [mediaError, setMediaError] = useState(false)
  const image = file.content_type.startsWith('image/')
  const pdf = file.content_type === 'application/pdf'
  const url = useFileUrl(file, image || pdf)
  const preview = useQuery({
    queryKey: ['quote-file-preview', file.id],
    queryFn: ({ signal }) => files.preview(file.id, signal),
    enabled: !image && !pdf,
  })
  const pending = image || pdf ? url.isPending : preview.isPending
  return (
    <>
      {pending ? (
        <p className="text-sm text-muted-foreground" role="status">
          Loading preview…
        </p>
      ) : null}
      {url.isError || preview.isError || mediaError ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-destructive" role="alert">
          Could not load this preview.
          <Button
            onClick={() => {
              setMediaError(false)
              if (image || pdf) void url.refetch()
              else void preview.refetch()
            }}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        </div>
      ) : null}
      {url.data && image && !mediaError ? (
        <img alt={file.original_file_name} className="mx-auto max-h-[62dvh] max-w-full object-contain" onError={() => setMediaError(true)} src={url.data.url} />
      ) : null}
      {url.data && pdf ? <PdfPreview key={url.data.url} name={file.original_file_name} url={url.data.url} /> : null}
      {!image && !pdf && preview.data ? (
        isSpreadsheet(file) ? (
          <SpreadsheetPreview text={preview.data.text} />
        ) : (
          <pre className="max-h-[62dvh] overflow-auto whitespace-pre-wrap break-words text-xs leading-5 [overflow-wrap:anywhere]">
            {preview.data.text || 'No text was found in this file.'}
          </pre>
        )
      ) : null}
    </>
  )
}

/** The file's name; opens its preview with Download (demo `FileLink`). */
export function FileLink({ file, small = false }: { file: QuoteFile; small?: boolean }) {
  const files = useQuoteFilesApi()
  const [open, setOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(false)
  const download = async () => {
    setDownloading(true)
    setDownloadError(false)
    try {
      const signed = await files.url(file.id)
      const response = await fetch(signed.url)
      if (!response.ok) throw new Error('Download failed')
      const objectUrl = URL.createObjectURL(await response.blob())
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = file.original_file_name
      document.body.append(anchor)
      anchor.click()
      anchor.remove()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch {
      setDownloadError(true)
    } finally {
      setDownloading(false)
    }
  }
  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <button
          className={cn(
            'flex min-w-0 items-center gap-3 rounded-md text-left text-sm font-medium outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50',
            small && 'border px-2 py-1',
          )}
          disabled={!file.ready}
          type="button"
        >
          {small ? <FileText aria-hidden="true" className="size-4 shrink-0" /> : <FileIcon file={file} />}
          <span className="min-w-0 [overflow-wrap:anywhere]">{file.original_file_name}</span>
        </button>
      </DialogTrigger>
      <DialogContent aria-describedby={undefined} className="flex max-h-[90dvh] w-[min(94vw,960px)] max-w-none flex-col gap-0 p-0 sm:max-w-none" showCloseButton={false}>
        <div className="flex items-start gap-2 border-b p-3">
          <DialogTitle className="min-w-0 flex-1 self-center break-words text-base [overflow-wrap:anywhere]">{file.original_file_name}</DialogTitle>
          <LoadingButton loading={downloading} loadingText="Downloading…" onClick={() => void download()} size="sm" variant="outline">
            <Download aria-hidden="true" />
            Download
          </LoadingButton>
          <Button aria-label="Close file preview" onClick={() => setOpen(false)} size="icon" variant="ghost">
            <X aria-hidden="true" />
          </Button>
        </div>
        <div className="min-h-24 overflow-auto p-3">
          {downloadError ? (
            <p className="mb-2 text-sm text-destructive" role="alert">
              Could not download this file. Try Download again.
            </p>
          ) : null}
          <FilePreview file={file} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

function FileRow({ file, quoteId, onImport }: { file: QuoteFile; quoteId?: string | null; onImport?: (file: QuoteFile) => void }) {
  const client = useQueryClient()
  const files = useQuoteFilesApi()
  const [confirm, setConfirm] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const remove = async () => {
    setPending(true)
    setError('')
    try {
      await files.remove(file.id)
      const queryKey = quoteFilesKey(quoteId)
      await client.cancelQueries({ queryKey, exact: true })
      client.setQueryData<QuoteFile[]>(queryKey, (list = []) => list.filter((item) => item.id !== file.id))
      client.removeQueries({ queryKey: ['quote-file-url', file.id], exact: true })
      client.removeQueries({ queryKey: ['quote-file-preview', file.id], exact: true })
      void client.invalidateQueries({ queryKey, exact: true })
    } catch (cause) {
      setError(getApiErrorMessage(cause, 'Could not remove this file. Try again.'))
    } finally {
      setPending(false)
    }
  }
  const importable = file.ready && IMPORTABLE.test(file.original_file_name)
  const tooLarge = file.file_size_bytes > MAX_IMPORT_BYTES
  return (
    <li className="grid gap-2 py-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <FileLink file={file} />
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatFileSize(file.file_size_bytes)}</span>
        <Button aria-label={`Remove ${file.original_file_name}`} disabled={pending} onClick={() => setConfirm(true)} size="icon" variant="ghost">
          <X aria-hidden="true" />
        </Button>
      </div>
      {!file.ready ? <p className="text-xs text-muted-foreground">Upload incomplete. Remove this file and add it again.</p> : null}
      {importable ? (
        <div className="flex flex-wrap items-center gap-2 pl-[3.25rem]">
          {/* Equipment import (USA-1029) supplies onImport; until then the action stays visible but disabled. */}
          <Button disabled={!onImport || pending || tooLarge} onClick={() => onImport?.(file)} size="sm" variant="outline">
            Import equipment
          </Button>
          {tooLarge ? <span className="text-xs text-muted-foreground">Import needs a file under 25 MB.</span> : null}
        </div>
      ) : null}
      {confirm ? (
        <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
          <span>Remove from the quote and the assistant?</span>
          <Button disabled={pending} onClick={() => setConfirm(false)} size="sm" variant="ghost">
            Cancel
          </Button>
          <LoadingButton loading={pending} loadingText="Removing…" onClick={() => void remove()} size="sm" variant="destructive">
            Remove file
          </LoadingButton>
        </div>
      ) : null}
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  )
}

/**
 * The Files library: drop zone, saved files with previews and download, uploads in flight.
 * `compact` is the assistant's collapsible list (demo `QuoteFiles compact`).
 */
export function QuoteFiles({
  quoteId,
  compact = false,
  disabled = false,
  onImport,
}: {
  quoteId?: string | null
  compact?: boolean
  disabled?: boolean
  onImport?: (file: QuoteFile) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const library = useQuoteFiles(quoteId)
  const files = library.files.data || []
  const pendingIds = new Set(library.uploads.map((item) => item.reservation?.file.id))
  const listed = files.filter((file) => !pendingIds.has(file.id))
  const count = listed.length + library.uploads.length

  const body = (
    <>
      {compact ? (
        <div className="flex items-center justify-end gap-2 py-2">
          <FilePicker add={library.add} disabled={disabled} />
        </div>
      ) : (
        <div
          className={cn(
            'mt-3 flex flex-col items-center justify-center rounded-lg border border-dashed px-4 py-8 text-center transition-colors',
            dragging ? 'border-foreground/40 bg-muted' : 'bg-muted/30',
          )}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false)
          }}
          onDragOver={(event) => {
            event.preventDefault()
            if (!disabled) setDragging(true)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            if (!disabled) void library.add(Array.from(event.dataTransfer.files))
          }}
        >
          <CloudUpload aria-hidden="true" className="mb-2 size-6 text-muted-foreground" />
          <p className="text-sm font-medium">Drop drawings, spreadsheets or photos</p>
          <input
            accept={fileAccept}
            aria-label="Choose quote files"
            className="sr-only"
            disabled={disabled}
            multiple
            onChange={(event) => {
              const chosen = Array.from(event.target.files || [])
              event.target.value = ''
              if (chosen.length) void library.add(chosen)
            }}
            ref={input}
            tabIndex={-1}
            type="file"
          />
          <Button className="mt-3" disabled={disabled} onClick={() => input.current?.click()} size="sm" variant="outline">
            Browse files
          </Button>
        </div>
      )}
      {library.files.isPending ? (
        <p className="py-2 text-sm text-muted-foreground" role="status">
          Loading files…
        </p>
      ) : null}
      {library.files.isError ? (
        <div className="flex flex-wrap items-center gap-2 py-2 text-sm text-destructive" role="alert">
          Could not load files.
          <Button onClick={() => void library.files.refetch()} size="sm" variant="outline">
            Retry
          </Button>
        </div>
      ) : null}
      {compact && !count && library.files.isSuccess ? <p className="py-2 text-sm text-muted-foreground">No files yet.</p> : null}
      <ul className="divide-y">
        {listed.map((file) => (
          <FileRow file={file} key={file.id} onImport={onImport} quoteId={quoteId} />
        ))}
      </ul>
      {library.uploads.map((item) => (
        <div className="grid gap-1.5 border-t py-3 text-sm" key={item.key}>
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">{item.source.name}</span>
            {item.status === 'uploading' ? (
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground" role="status">
                {item.progress === 100 ? 'Checking…' : `${item.progress}%`}
              </span>
            ) : (
              <>
                <Button onClick={() => void library.retry(item.key)} size="sm" variant="outline">
                  Retry
                </Button>
                <Button aria-label={`Remove failed upload ${item.source.name}`} onClick={() => void library.dismiss(item.key)} size="icon" variant="ghost">
                  <X aria-hidden="true" />
                </Button>
              </>
            )}
          </div>
          {item.status === 'uploading' ? (
            <Progress aria-label={`Uploading ${item.source.name}`} className="h-1" value={item.progress} />
          ) : (
            <p className="text-destructive" role="alert">
              {item.error}
            </p>
          )}
        </div>
      ))}
    </>
  )

  if (compact) {
    return (
      <details className="group mb-2" onToggle={(event) => setExpanded(event.currentTarget.open)} open={expanded || library.uploads.length > 0}>
        <summary className="flex cursor-pointer list-none items-center gap-1 py-1 text-sm font-medium text-muted-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
          <ChevronDown aria-hidden="true" className="size-4 transition-transform group-open:rotate-180" />
          Files ({count})
        </summary>
        <div className="max-h-64 overflow-y-auto">{body}</div>
      </details>
    )
  }
  return (
    <section aria-label="Files" className="rounded-xl border bg-card p-4">
      <h2 className="text-base font-semibold">
        Files <span className="font-normal tabular-nums text-muted-foreground">({count})</span>
      </h2>
      {body}
    </section>
  )
}
